import { useApiEntity } from '../../hooks/use-api-entity'
import { PageHeader, FormField, Alert, btnPrimary, btnSecondary, card, input, colors } from '../../components/ui'

interface QcLotForm {
  lotNumber: string
  testType: string
  analyte: string
  manufacturer: string
  level: string
  targetValue: string
  targetSd: string
  unit: string
  expirationDate: string
}

const defaults: QcLotForm = {
  lotNumber: '',
  testType: '',
  analyte: '',
  manufacturer: '',
  level: 'normal',
  targetValue: '',
  targetSd: '',
  unit: '',
  expirationDate: '',
}

export function QcLotEditPage() {
  const { data, isNew, loading, saving, error, success, save, cancel, update } = useApiEntity<QcLotForm>({
    path: '/qc/lots',
    listPath: '/qc/lots',
    defaultValue: defaults,
    // Phase 5.9.6: marshal string form state into the shape the
    // backend expects. targetValue/targetSd are numeric columns;
    // expirationDate is an ISO timestamp.
    transformPayload: (form) => {
      const payload: Record<string, unknown> = { ...form }
      const targetValueNum = form.targetValue ? Number(form.targetValue) : undefined
      const targetSdNum = form.targetSd ? Number(form.targetSd) : undefined
      if (targetValueNum !== undefined) {
        if (Number.isNaN(targetValueNum)) throw new Error('Target value must be a number')
        payload.targetValue = targetValueNum
      }
      if (targetSdNum !== undefined) {
        if (Number.isNaN(targetSdNum)) throw new Error('Target SD must be a number')
        payload.targetSd = targetSdNum
      }
      if (form.expirationDate) {
        // HTML date input produces YYYY-MM-DD; normalize to end-of-day UTC
        // so the lot counts as active through the full expiration day.
        payload.expirationDate = `${form.expirationDate}T23:59:59.000Z`
      } else {
        delete payload.expirationDate
      }
      return payload
    },
  })

  const handleSave = async () => {
    await save()
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  return (
    <div>
      <PageHeader
        title={isNew ? 'New QC Lot' : `QC Lot: ${data.lotNumber}`}
        backTo="/qc/lots"
        backLabel="Back to Lots"
        actions={
          <>
            <button onClick={cancel} style={btnSecondary}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      />
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      <div style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <FormField
          name="lotNumber"
          label="Lot Number"
          required
          value={data.lotNumber}
          onChange={(v) => update('lotNumber', v)}
          disabled={!isNew}
        />
        <FormField
          name="testType"
          label="Test Type"
          required
          value={data.testType}
          onChange={(v) => update('testType', v)}
        />
        <FormField
          name="analyte"
          label="Analyte"
          required
          value={data.analyte}
          onChange={(v) => update('analyte', v)}
          placeholder="e.g. SARS-CoV-2 Ag"
        />
        <FormField
          name="manufacturer"
          label="Manufacturer"
          value={data.manufacturer}
          onChange={(v) => update('manufacturer', v)}
          placeholder="e.g. Bio-Rad"
        />
        <FormField name="level" label="Level" required>
          <select style={input} value={data.level} onChange={(e) => update('level', e.target.value)}>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
        </FormField>
        <FormField
          name="unit"
          label="Unit"
          value={data.unit}
          onChange={(v) => update('unit', v)}
          placeholder="e.g. S/CO, mg/dL"
        />
        <FormField
          name="targetValue"
          label="Target Value"
          required
          type="number"
          value={data.targetValue}
          onChange={(v) => update('targetValue', v)}
        />
        <FormField
          name="targetSd"
          label="Target SD"
          required
          type="number"
          value={data.targetSd}
          onChange={(v) => update('targetSd', v)}
        />
        <FormField
          name="expirationDate"
          label="Expiration Date"
          type="date"
          value={data.expirationDate}
          onChange={(v) => update('expirationDate', v)}
        />
      </div>
    </div>
  )
}
